import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Linkedin, Menu, X, Info, Quote } from "lucide-react";
import { BIZ, PRACTICE_AREAS, JOURNEY, CONSULT_STEPS, REVIEW } from "./berguiaData";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import LawInquiryForm from "@/components/LawInquiryForm";

const CHARCOAL = "#17181A";
const BRASS = "#B98D4B";
const CREAM = "#F6F1E8";
const INK = "#211F1E";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function BerguiaLawSite() {
  const [open, setOpen] = useState(false);
  const tagalog = typeof window !== "undefined" && window.localStorage.getItem("mysitecare-language") === "tl";
  const links = [["Journey", "#journey"], ["Practice", "#practice"], ["Consultation", "#consultation"], ["Office", "#office"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Inter', sans-serif" }} data-testid="berguia-law-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap'); .bg-serif{font-family:'Playfair Display',serif;}`}</style>

      <div data-testid="law-disclaimer" className="bg-black text-white text-center text-xs py-2.5 px-4">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: BRASS }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 backdrop-blur shadow-sm" style={{ background: "rgba(23,24,26,0.97)" }}>
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#top" data-testid="berguia-logo" className="bg-serif text-base text-white tracking-wide">BERGUIA LAW</a>
          <div className="hidden lg:flex items-center gap-7">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-white/70 hover:text-white transition-colors">{l}</a>)}
          </div>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="berguia-menu-toggle" aria-label="Menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1" style={{ background: CHARCOAL }}>{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium text-white/80">{l}</a>)}</div>}
      </header>

      {/* HERO — split screen, not full-bleed background */}
      <section className="grid lg:grid-cols-[1fr_1fr]" data-testid="berguia-hero">
        <div className="flex items-center px-6 sm:px-10 lg:px-14 py-16 lg:py-0 order-2 lg:order-1" style={{ background: CHARCOAL, minHeight: "min(85vh,760px)" }}>
          <motion.div {...fadeUp} className="max-w-md">
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: BRASS }}>Ecoland, Matina — Davao City</span>
            <h1 className="mt-6 text-4xl sm:text-5xl bg-serif text-white leading-[1.08]">From the Wards<br />to the Bar</h1>
            <p className="mt-6 text-white/70 text-base leading-relaxed">I'm a Registered Nurse who became a Davao lawyer. Civil, criminal and family law representation — built on my own path, not a corporate ladder.</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#consultation" data-testid="berguia-hero-cta" className="rounded-full px-7 py-3.5 text-sm font-semibold" style={{ background: BRASS, color: CHARCOAL }}>Request a Consultation</a>
              <a href={`tel:${BIZ.phone}`} data-testid="berguia-hero-call" className="rounded-full px-7 py-3.5 text-sm font-semibold text-white border border-white/30">Call {BIZ.phoneDisplay}</a>
            </div>
          </motion.div>
        </div>
        <div className="order-1 lg:order-2 relative min-h-[42vh] lg:min-h-0">
          <img src="/law/berguia-law-davao-concept/reception-standing.jpg" alt="Atty. Paul Jordan T. Berguia at the Berguia Law office" className="absolute inset-0 w-full h-full object-cover object-top" />
        </div>
      </section>

      {/* JOURNEY — the narrative spine of the page, promoted right after the hero */}
      <section id="journey" className="py-20" style={{ background: CREAM }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.img {...fadeUp} src="/law/berguia-law-davao-concept/profile.jpg" alt="Atty. Paul Jordan T. Berguia" className="mx-auto h-24 w-24 rounded-full object-cover shadow-md mb-6" />
          <motion.h2 {...fadeUp} className="bg-serif text-3xl text-center" style={{ color: CHARCOAL }}>The Professional Journey</motion.h2>
          <div className="mt-16 relative">
            <div className="hidden sm:block absolute left-0 right-0 top-5 h-px" style={{ background: "rgba(0,0,0,0.12)" }} />
            <div className="grid sm:grid-cols-5 gap-10 sm:gap-4">
              {JOURNEY.map((j, i) => (
                <motion.div key={j.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} className="relative">
                  <div className="hidden sm:flex h-10 w-10 rounded-full items-center justify-center text-xs font-bold relative z-10" style={{ background: BRASS, color: CHARCOAL }}>{i + 1}</div>
                  {j.year && <div className="mt-4 text-xs font-semibold uppercase tracking-wide" style={{ color: BRASS }}>{j.year}</div>}
                  <div className="mt-1 font-semibold text-sm" style={{ color: CHARCOAL }}>{j.title}</div>
                  <p className="mt-1.5 text-xs leading-relaxed" style={{ color: "#5A5650" }}>{j.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.p {...fadeUp} className="mt-14 text-center text-sm max-w-2xl mx-auto" style={{ color: "#5A5650" }}>My professional profile also lists an affiliation with The Rizal Memorial Colleges, Inc. and continuing legal and AI-related training.</motion.p>
        </div>
      </section>

      {/* PRACTICE AREAS — numbered full-width rows, not a card grid */}
      <section id="practice" className="py-20" style={{ background: CHARCOAL }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="bg-serif text-3xl text-white mb-3">What I Handle</motion.h2>
          <motion.p {...fadeUp} className="text-sm text-white/60 mb-12">Community recommendations specifically mention annulment and family-law work — I treat that as supporting evidence, not a formal specialization.</motion.p>
          <div className="divide-y divide-white/10">
            {PRACTICE_AREAS.map((s, i) => (
              <motion.div key={s.title} {...fadeUp} className="py-6 flex items-start gap-6">
                <span className="bg-serif text-3xl shrink-0 w-12" style={{ color: BRASS }}>{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-semibold text-base text-white">{s.title}</h3>
                  <p className="mt-1 text-sm text-white/60">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONSULTATION — steps + form side by side */}
      <section id="consultation" className="py-20" style={{ background: "#ECE3D3" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.85fr_1fr] gap-14">
          <motion.div {...fadeUp}>
            <h2 className="bg-serif text-3xl mb-10" style={{ color: CHARCOAL }}>How Consultation Works</h2>
            <div className="space-y-8">
              {CONSULT_STEPS.map((s) => (
                <div key={s.n} className="flex items-start gap-5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-serif text-sm" style={{ background: CHARCOAL, color: BRASS }}>{s.n}</div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: CHARCOAL }}>{s.title}</p>
                    <p className="mt-1 text-xs" style={{ color: "#5A5650" }}>{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <LawInquiryForm
              testidPrefix="berguia"
              businessName={BIZ.name}
              accent={BRASS}
              accentText={CHARCOAL}
              phone={BIZ.phone}
              phoneDisplay={BIZ.phoneDisplay}
            />
          </motion.div>
        </div>
      </section>

      {/* CLIENT FEEDBACK — large editorial pull-quote */}
      <section className="py-20" style={{ background: CREAM }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <Quote size={34} style={{ color: BRASS }} className="mx-auto mb-6" />
          <motion.p {...fadeUp} className="bg-serif text-2xl sm:text-3xl leading-snug" style={{ color: CHARCOAL }}>"{REVIEW.quote}"</motion.p>
          <motion.a {...fadeUp} href={REVIEW.sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-6 inline-block text-xs underline" style={{ color: "#8A8478" }}>{REVIEW.source} · {REVIEW.date}</motion.a>
          <motion.p {...fadeUp} className="mt-3 text-xs" style={{ color: "#8A8478" }}>{tagalog ? `${REVIEW.rating}★ sa ${REVIEW.reviewCountLabel}.` : `${REVIEW.rating}★ across ${REVIEW.reviewCountLabel}.`}</motion.p>
        </div>
      </section>

      {/* OFFICE GALLERY — real photos of the Davao office */}
      <section className="grid grid-cols-2 sm:grid-cols-4">
        {[
          ["office-desk-work.jpg", "Atty. Berguia at his desk in the Davao office"],
          ["office-gallery-wall.jpg", "Atty. Berguia in the office, near the interior artwork"],
          ["office-wide.jpg", "A wide view of the Berguia Law office"],
          ["office-reception.jpg", "The Berguia Law reception desk"],
        ].map(([file, alt]) => (
          <img key={file} src={`/law/berguia-law-davao-concept/${file}`} alt={alt} className="w-full h-40 sm:h-56 object-cover" />
        ))}
      </section>

      {/* OFFICE */}
      <section id="office" className="py-20" style={{ background: CHARCOAL }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="bg-serif text-3xl text-white">Davao Office</h2>
            <ul className="mt-7 space-y-5 text-sm text-white/75">
              <li className="flex items-start gap-4"><MapPin size={20} style={{ color: BRASS }} className="mt-0.5 shrink-0" />{BIZ.address}</li>
              <li className="flex items-start gap-4"><Phone size={20} style={{ color: BRASS }} className="mt-0.5 shrink-0" /><a href={`tel:${BIZ.phone}`} data-testid="berguia-phone-link" className="hover:underline">{BIZ.phoneDisplay}</a></li>
              <li className="flex items-start gap-4"><Linkedin size={20} style={{ color: BRASS }} className="mt-0.5 shrink-0" /><a href={BIZ.linkedin} target="_blank" rel="noopener noreferrer" data-testid="berguia-linkedin-link" className="hover:underline">Professional profile on LinkedIn</a></li>
            </ul>
            <p className="mt-6 text-xs text-white/40">Please confirm office hours directly before visiting — public hours were not confirmed in research.</p>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[280px] rounded-lg overflow-hidden shadow-md">
            <iframe title="Berguia Law office map" src={BIZ.mapEmbed} className="w-full h-full min-h-[280px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </motion.div>
        </div>
      </section>

      <footer className="py-8" style={{ background: CHARCOAL, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs text-white/50">
          <img src="/law/berguia-law-davao-concept/banner.jpg" alt="Atty. Paul Jordan T. Berguia, Lawyer | Notary Public" className="h-10 w-auto object-contain rounded" />
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm current details before visiting.</p>
        </div>
      </footer>

      <ConceptCTAPill slug="berguia-law-davao-concept" />
    </div>
  );
}
